import { CALL_API, getJSON } from 'redux-api-middleware';
import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';
import { USER_API } from '../../../../config/endpoints';
import { ROUTE } from '../../middleware/route';
import { TRACK_EVENT } from '../../middleware/analytics';
import { GLOBAL_LOGIN, GLOBAL_LOGOUT } from '../../../../server/services/analytics-events';
import { UPDATE_USER_RECEIVE } from '../user';
import jwtParser from '../../../../utils/jwt-parser';
import jwtSub from '../../../../utils/jwt-sub';
import secsToMs from '../../../../utils/secs-to-ms';
import paths from '../../../routes/paths';

// Note: some auth state can be overriden by SSO cookies at the start of a request

// ACTIONS
export const ACCESS_START = Symbol('ACCESS_START');
export const ACCESS_START_REQUEST = 'AUTH/ACCESS_START_REQUEST';
export const ACCESS_START_FAILURE = 'AUTH/ACCESS_START_FAILURE';
export const ACCESS_START_RECEIVE = 'AUTH/ACCESS_START_RECEIVE';

export const ACCESS_COMPLETE = Symbol('ACCESS_COMPLETE');
export const ACCESS_COMPLETE_REQUEST = 'AUTH/ACCESS_COMPLETE_REQUEST';
export const ACCESS_COMPLETE_FAILURE = 'AUTH/ACCESS_COMPLETE_FAILURE';
export const ACCESS_COMPLETE_RECEIVE = 'AUTH/ACCESS_COMPLETE_RECEIVE';

export const LOGOUT = Symbol('LOGOUT');
export const LOGOUT_RECEIVE = 'AUTH/LOGOUT_RECEIVE';
export const LOGOUT_FAILURE = 'AUTH/LOGOUT_FAILURE';
export const LOGOUT_REQUEST = 'AUTH/LOGOUT_REQUEST';

export const REFRESH = Symbol('REFRESH');
export const REFRESH_REQUEST = 'AUTH/REFRESH_REQUEST';
export const REFRESH_FAILURE = 'AUTH/REFRESH_FAILURE';
export const REFRESH_RECEIVE = 'AUTH/REFRESH_RECEIVE';

export const AUTHENTICATION_ERROR = 'AUTH/AUTHENTICATION_ERROR';

export const SET_PHARMACY_ID = 'AUTH/SET_PHARMACY_ID';

// ACTION CREATORS
export const accessStart = (email, apiEndpoint = USER_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/access/start`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      target: 'pharmacy-web',
    }),
    types: [
      {
        type: ACCESS_START_REQUEST,
        meta: { email },
      },
      ACCESS_START_RECEIVE,
      ACCESS_START_FAILURE,
    ],
    options: { authStage: ACCESS_START },
  },
});

export const accessCompletePayload = (action, state, res) => getJSON(res.clone());
export const accessCompleteMeta = async (action, state, res) => {
  let isPasswordSet = false;
  try {
    ({ isPasswordSet } = await getJSON(res.clone()));
  } catch (err) {
    console.log(err);
  }

  return { [ROUTE]: isPasswordSet ? '/' : paths.signup.password };
};

export const accessComplete = (rnd, email, apiEndpoint = USER_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/access/complete/${rnd}`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
    }),
    types: [
      ACCESS_COMPLETE_REQUEST,
      {
        type: ACCESS_COMPLETE_RECEIVE,
        payload: accessCompletePayload,
        meta: accessCompleteMeta,
        [TRACK_EVENT]: GLOBAL_LOGIN,
      },
      { type: ACCESS_COMPLETE_FAILURE },
    ],
    options: { authStage: ACCESS_COMPLETE },
  },
});

export const authenticationError = () => ({
  type: AUTHENTICATION_ERROR,
});

export const logout = (apiEndpoint = USER_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/logout`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    // Body assigned by middleware
    types: [
      LOGOUT_REQUEST,
      {
        type: LOGOUT_RECEIVE, [ROUTE]: '/login', [TRACK_EVENT]: GLOBAL_LOGOUT,
      },
      {
        type: LOGOUT_FAILURE, [ROUTE]: '/login', [TRACK_EVENT]: GLOBAL_LOGOUT,
      },
    ],
    options: { authStage: LOGOUT },
  },
});

export const refresh = (apiEndpoint = USER_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/refresh`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    // Body assigned by middleware
    types: [REFRESH_REQUEST, REFRESH_RECEIVE, REFRESH_FAILURE],
    options: { authStage: REFRESH },
  },
});

export const setWardlesPharmacy = () => ({
  type: SET_PHARMACY_ID,
  payload: {
    id: 'wardles',
  },
});

export const clearPharmacy = () => ({
  type: SET_PHARMACY_ID,
  payload: {
    id: null,
  },
});

const inflateUnixTime = (unixTime)  => unixTime ? unixTime * 1000 : 0;

// SELECTORS
export const getIsAuthenticated = state => state && state.idToken !== null;
export const getAccessStartToken = state => state.accessStartToken;
export const getAccessToken = state => state.accessToken;
export const getIdToken = state => state.idToken;
export const getIdTokenExpiration = state => state.idToken != null ? inflateUnixTime(jwtParser(state.idToken).exp)  : 0;
export const getRefreshToken = state => state.refreshToken;
export const getRefreshTokenExpiration = state => state.refreshTokenExpiration;
export const getEmail = state => (state.idToken != null ? jwtParser(state.idToken).email : null);
export const getUserId = state => jwtSub(state.idToken) || null;
export const getPharmacyId = state => state.pharmacyId;

// REDUCERS
export const accessStartToken = handleActions({
  [ACCESS_START_RECEIVE]: (state, action) => action.payload.token,
  [LOGOUT_RECEIVE]: () => null,
  [LOGOUT_FAILURE]: () => null,
  [ACCESS_COMPLETE_FAILURE]: (state, action) => action.payload.response.token || null,
}, null);

export const idToken = handleActions({
  [ACCESS_COMPLETE_RECEIVE]: (state, action) => action.payload.idToken || null,
  [AUTHENTICATION_ERROR]: () => null,
  [REFRESH_RECEIVE]: (state, action) => action.payload.idToken || null,
  [REFRESH_FAILURE]: () => null,
  [LOGOUT_RECEIVE]: () => null,
  [LOGOUT_FAILURE]: () => null,
}, null);

export const accessToken = handleActions({
  [ACCESS_COMPLETE_RECEIVE]: (state, action) => action.payload.accessToken || null,
  [REFRESH_RECEIVE]: (state, action) => action.payload.accessToken || null,
  [REFRESH_FAILURE]: () => null,
  [LOGOUT_RECEIVE]: () => null,
  [LOGOUT_FAILURE]: () => null,
}, null);

export const refreshToken = handleActions({
  [ACCESS_COMPLETE_RECEIVE]: (state, action) => action.payload.refreshToken,
  [LOGOUT_RECEIVE]: () => null,
  [LOGOUT_FAILURE]: () => null,
  [AUTHENTICATION_ERROR]: () => null,
  [REFRESH_FAILURE]: () => null,
}, null);

export const refreshTokenExpiration = handleActions({
  [ACCESS_COMPLETE_RECEIVE]: () => new Date(Date.now() + secsToMs(process.env.REFRESH_TOKEN_LIFE_SECS)),
  [LOGOUT_RECEIVE]: () => null,
  [AUTHENTICATION_ERROR]: () => null,
  [REFRESH_FAILURE]: () => null,
}, null);

export const pharmacyId = handleActions({
  [SET_PHARMACY_ID]: (state, action) => action.payload.id,
  [UPDATE_USER_RECEIVE]: () => null,
}, null);

export default combineReducers({
  accessStartToken,
  accessToken,
  idToken,
  refreshToken,
  refreshTokenExpiration,
  pharmacyId
});
